import { emptySplitApi as api } from "./emptyApi"
import type { InitiativeCategory } from "~/ui-rtk/constants/initiative-types"
import type {
  CustomAttributionControllerCreateModelApiResponse,
  CustomAttributionControllerCreateModelApiArg,
  CustomAttributionControllerGetAllModelsApiResponse,
  CustomAttributionControllerGetAllModelsApiArg,
  CustomAttributionControllerGetModelByIdApiResponse,
  CustomAttributionControllerGetModelByIdApiArg,
  CustomAttributionControllerUpdateModelApiResponse,
  CustomAttributionControllerUpdateModelApiArg,
  CustomAttributionControllerDeleteModelApiResponse,
  CustomAttributionControllerDeleteModelApiArg,
  CustomAttributionControllerCreateWeightApiResponse,
  CustomAttributionControllerCreateWeightApiArg,
  CustomAttributionControllerGetWeightsByModelApiResponse,
  CustomAttributionControllerGetWeightsByModelApiArg,
  CustomAttributionControllerUpdateWeightApiResponse,
  CustomAttributionControllerUpdateWeightApiArg,
  CustomAttributionControllerDeleteWeightApiResponse,
  CustomAttributionControllerDeleteWeightApiArg,
  CustomAttributionControllerBulkUpdateWeightsApiResponse,
  CustomAttributionControllerBulkUpdateWeightsApiArg,
  CustomAttributionModelDto,
} from "./types/custom-attribution.types"

/**
 * Initiative type descriptor returned by
 * `GET /custom-attribution/initiative-types`. Covers predefined types
 * (color null) and custom types (stored color family + isCustom true).
 */
export interface CustomInitiativeTypeOption {
  id: string | null
  value: string
  label: string
  isCustom: boolean
  category: InitiativeCategory
  color: string | null
}

const injectedRtkApi = api.injectEndpoints({
  endpoints: build => ({
    customAttributionControllerGetInitiativeTypes: build.query<
      CustomInitiativeTypeOption[],
      void
    >({
      query: () => ({ url: `/custom-attribution/initiative-types` }),
      providesTags: ["CustomAttributionInitiativeTypes"],
    }),
    customAttributionControllerCreateInitiativeType: build.mutation<
      CustomInitiativeTypeOption,
      { label: string; category: InitiativeCategory }
    >({
      query: queryArg => ({
        url: `/custom-attribution/initiative-types`,
        method: "POST",
        body: { label: queryArg.label, category: queryArg.category },
      }),
      invalidatesTags: ["CustomAttributionInitiativeTypes"],
    }),
    customAttributionControllerDeleteInitiativeType: build.mutation<
      CustomInitiativeTypeOption,
      { id: string }
    >({
      query: queryArg => ({
        url: `/custom-attribution/initiative-types/${queryArg.id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["CustomAttributionInitiativeTypes"],
    }),
    customAttributionControllerGetAllModels: build.query<
      CustomAttributionControllerGetAllModelsApiResponse,
      CustomAttributionControllerGetAllModelsApiArg
    >({
      query: () => ({ url: `/custom-attribution/model` }),
      providesTags: ["CustomAttributionModels"],
    }),
    customAttributionControllerCreateModel: build.mutation<
      CustomAttributionControllerCreateModelApiResponse,
      CustomAttributionControllerCreateModelApiArg
    >({
      query: queryArg => ({
        url: `/custom-attribution/model`,
        method: "POST",
        body: queryArg.createCustomAttributionModelDto,
      }),
      invalidatesTags: ["CustomAttributionModels"],
    }),
    customAttributionControllerGetModelById: build.query<
      CustomAttributionControllerGetModelByIdApiResponse,
      CustomAttributionControllerGetModelByIdApiArg
    >({
      query: queryArg => ({ url: `/custom-attribution/model/${queryArg.id}` }),
      providesTags: ["CustomAttributionModels"],
    }),
    customAttributionControllerUpdateModel: build.mutation<
      CustomAttributionControllerUpdateModelApiResponse,
      CustomAttributionControllerUpdateModelApiArg
    >({
      query: queryArg => ({
        url: `/custom-attribution/model/${queryArg.id}`,
        method: "PUT",
        body: queryArg.updateCustomAttributionModelDto,
      }),
      invalidatesTags: ["CustomAttributionModels"],
    }),
    customAttributionControllerDeleteModel: build.mutation<
      CustomAttributionControllerDeleteModelApiResponse,
      CustomAttributionControllerDeleteModelApiArg
    >({
      query: queryArg => ({
        url: `/custom-attribution/model/${queryArg.id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["CustomAttributionModels"],
    }),
    customAttributionControllerGetWeightsByModel: build.query<
      CustomAttributionControllerGetWeightsByModelApiResponse,
      CustomAttributionControllerGetWeightsByModelApiArg
    >({
      query: queryArg => ({
        url: `/custom-attribution/model/${queryArg.modelId}/weight`,
      }),
      providesTags: ["CustomAttributionWeights"],
    }),
    customAttributionControllerCreateWeight: build.mutation<
      CustomAttributionControllerCreateWeightApiResponse,
      CustomAttributionControllerCreateWeightApiArg
    >({
      query: queryArg => ({
        url: `/custom-attribution/model/${queryArg.modelId}/weight`,
        method: "POST",
        body: queryArg.upsertCustomAttributionWeightDto,
      }),
      invalidatesTags: ["CustomAttributionWeights"],
    }),
    customAttributionControllerUpdateWeight: build.mutation<
      CustomAttributionControllerUpdateWeightApiResponse,
      CustomAttributionControllerUpdateWeightApiArg
    >({
      query: queryArg => ({
        url: `/custom-attribution/model/${queryArg.modelId}/weight/${queryArg.id}`,
        method: "PUT",
        body: queryArg.upsertCustomAttributionWeightDto,
      }),
      invalidatesTags: ["CustomAttributionWeights"],
    }),
    customAttributionControllerDeleteWeight: build.mutation<
      CustomAttributionControllerDeleteWeightApiResponse,
      CustomAttributionControllerDeleteWeightApiArg
    >({
      query: queryArg => ({
        url: `/custom-attribution/model/${queryArg.modelId}/weight/${queryArg.id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["CustomAttributionWeights"],
    }),
    customAttributionControllerBulkUpdateWeights: build.mutation<
      CustomAttributionControllerBulkUpdateWeightsApiResponse,
      CustomAttributionControllerBulkUpdateWeightsApiArg
    >({
      query: queryArg => ({
        url: `/custom-attribution/model/${queryArg.modelId}/weight/bulk`,
        method: "POST",
        body: queryArg,
      }),
      invalidatesTags: ["CustomAttributionWeights"],
    }),
    customAttributionControllerCreateMediaTarget: build.mutation<
      unknown,
      {
        companyId: string
        initiativeType: string
        type: string
        campaign_id: string
        ad_set_id?: string
      }
    >({
      query: queryArg => ({
        url: `/custom-attribution/media-target`,
        method: "POST",
        body: {
          companyId: queryArg.companyId,
          initiativeType: queryArg.initiativeType,
          type: queryArg.type,
          campaign_id: queryArg.campaign_id,
          ...(queryArg.ad_set_id && { ad_set_id: queryArg.ad_set_id }),
        },
      }),
      invalidatesTags: [
        "CustomAttributionModels",
        "CustomAttributionMediaTargets",
      ],
    }),
    customAttributionControllerUpdateMediaTarget: build.mutation<
      unknown,
      {
        id: string
        companyId: string
        initiativeType: string
        type: string
        campaign_id: string
        ad_set_id?: string
      }
    >({
      query: queryArg => ({
        url: `/custom-attribution/media-target/${queryArg.id}`,
        method: "PUT",
        body: {
          companyId: queryArg.companyId,
          initiativeType: queryArg.initiativeType,
          type: queryArg.type,
          campaign_id: queryArg.campaign_id,
          ...(queryArg.ad_set_id && { ad_set_id: queryArg.ad_set_id }),
        },
      }),
      invalidatesTags: [
        "CustomAttributionModels",
        "CustomAttributionMediaTargets",
      ],
    }),
    customAttributionControllerGetAllMediaTargets: build.query<unknown[], void>(
      {
        query: () => ({ url: `/custom-attribution/media-targets` }),
        providesTags: ["CustomAttributionMediaTargets"],
      },
    ),
    customAttributionControllerGetMediaTargetsByModel: build.query<
      unknown[],
      { modelId: string }
    >({
      query: queryArg => ({
        url: `/custom-attribution/model/${queryArg.modelId}/media-target`,
      }),
      providesTags: ["CustomAttributionMediaTargets"],
    }),
    customAttributionControllerDeleteMediaTarget: build.mutation<
      unknown,
      {
        id: string
      }
    >({
      query: queryArg => ({
        url: `/custom-attribution/media-target/${queryArg.id}`,
        method: "DELETE",
      }),
      invalidatesTags: [
        "CustomAttributionModels",
        "CustomAttributionMediaTargets",
      ],
    }),
    customAttributionControllerClassifyCampaigns: build.mutation<
      {
        classifications: Array<{
          campaign_id: string
          initiative_type: string
          reason: string
        }>
      },
      {
        campaigns: Array<{
          campaign_id: string
          campaign_name: string
          media_channel: string
          optimization_goal?: string
        }>
      }
    >({
      query: queryArg => ({
        url: `/custom-attribution/classify-campaigns`,
        method: "POST",
        body: queryArg,
      }),
    }),
    customAttributionControllerBulkCreateMediaTargets: build.mutation<
      unknown[],
      {
        targets: Array<{
          initiativeType: string
          type: string
          campaign_id: string
          ad_set_id?: string
        }>
      }
    >({
      query: queryArg => ({
        url: `/custom-attribution/media-target/bulk`,
        method: "POST",
        body: queryArg,
      }),
      invalidatesTags: [
        "CustomAttributionModels",
        "CustomAttributionMediaTargets",
      ],
    }),
  }),
  overrideExisting: false,
})

export { injectedRtkApi as customAttributionApi }

// Export hooks for usage in components
export const {
  useCustomAttributionControllerGetAllModelsQuery,
  useCustomAttributionControllerCreateModelMutation,
  useCustomAttributionControllerGetModelByIdQuery,
  useCustomAttributionControllerUpdateModelMutation,
  useCustomAttributionControllerDeleteModelMutation,
  useCustomAttributionControllerGetWeightsByModelQuery,
  useCustomAttributionControllerCreateWeightMutation,
  useCustomAttributionControllerUpdateWeightMutation,
  useCustomAttributionControllerDeleteWeightMutation,
  useCustomAttributionControllerBulkUpdateWeightsMutation,
  useCustomAttributionControllerCreateMediaTargetMutation,
  useCustomAttributionControllerUpdateMediaTargetMutation,
  useCustomAttributionControllerDeleteMediaTargetMutation,
  useCustomAttributionControllerGetAllMediaTargetsQuery,
  useCustomAttributionControllerGetMediaTargetsByModelQuery,
  useCustomAttributionControllerClassifyCampaignsMutation,
  useCustomAttributionControllerBulkCreateMediaTargetsMutation,
  useCustomAttributionControllerGetInitiativeTypesQuery,
  useCustomAttributionControllerCreateInitiativeTypeMutation,
  useCustomAttributionControllerDeleteInitiativeTypeMutation,
} = injectedRtkApi

// Re-export types needed by components
export type {
  CustomAttributionModelDto,
  CreateCustomAttributionModelDto,
  UpdateCustomAttributionModelDto,
  CustomAttributionWeightDto,
  UpsertCustomAttributionWeightDto,
} from "./types/custom-attribution.types"

// Add weights to CustomAttributionModelDto
export interface CustomAttributionModelWithWeights
  extends CustomAttributionModelDto {
  weights: Array<{
    id: string
    modelType: string
    source: string
    metric: string
    weight: number
  }>
}
